import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Button, Table, Badge, Modal,
  Form, Spinner, InputGroup, Alert
} from 'react-bootstrap';
import {
  Clock, Plus, Check, X, ChevronLeft, ChevronRight,
  Download, AlertCircle
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const STATUS_VARIANT = { pending: 'warning', approved: 'success', rejected: 'danger' };
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ── FIX #5: Safe local date — never pass through new Date() which shifts UTC ──
// "2026-05-13" → "13 May" correctly regardless of timezone
const formatDateSafe = (dateStr, opts = {}) => {
  if (!dateStr) return '—';
  const s = String(dateStr).slice(0, 10); // ensure "YYYY-MM-DD"
  const [y, m, d] = s.split('-').map(Number);
  // Use Date(year, month-1, day) — LOCAL constructor, no UTC shift
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', opts);
};

// ── Get today's date as YYYY-MM-DD in local timezone (not UTC) ───────────────
const getLocalToday = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

// ── Get local date N days ago as YYYY-MM-DD ──────────────────────────────────
const getLocalDateBefore = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

// ── Confirm Modal ────────────────────────────────────────────────────────────
const ConfirmModal = ({
  show, onHide, onConfirm, loading, title, message,
  confirmLabel, variant = 'primary', showRemarks = false, remarks, onRemarksChange
}) => (
  <Modal show={show} onHide={onHide} centered size="sm">
    <Modal.Header closeButton className="border-0">
      <Modal.Title className="fw-bold fs-6">{title}</Modal.Title>
    </Modal.Header>
    <Modal.Body className="py-3">
      <p className="text-muted mb-0 text-center" style={{ fontSize: 14 }}>{message}</p>
      {showRemarks && (
        <Form.Group className="mt-3">
          <Form.Label className="small fw-bold">Remarks (optional)</Form.Label>
          <Form.Control
            as="textarea" rows={2} placeholder="Add a note for the employee…"
            value={remarks || ''} onChange={e => onRemarksChange(e.target.value)}
          />
        </Form.Group>
      )}
    </Modal.Body>
    <Modal.Footer className="border-0 justify-content-center gap-2">
      <Button variant="light" onClick={onHide} disabled={loading}>Cancel</Button>
      <Button variant={variant} onClick={onConfirm} disabled={loading}>
        {loading && <Spinner size="sm" className="me-1" />}{confirmLabel}
      </Button>
    </Modal.Footer>
  </Modal>
);

// ── Approver roles — can both approve AND self-submit ────────────────────────
const APPROVER_ROLES = ['admin', 'super-admin', 'hr-manager', 'account-manager'];

const TimesheetPage = () => {
  const { user } = useAuth();

  // FIX #4: Everyone can approve their reports; everyone can self-submit.
  // canApprove: admin/hr see all timesheets and can approve
  // canSelfSubmit: ALL roles can log their own hours
  // FIX: All non-employees can approve. Employee-role users who are reporting
  // managers (have direct reports) also get approve access.
  const [hasDirectReports, setHasDirectReports] = useState(false);

  useEffect(() => {
    if (user?.role === 'employee') {
      axiosInstance.get('/employees/my-reports')
        .then(r => setHasDirectReports((r.data.data || []).length > 0))
        .catch(() => {});
    }
  }, [user?.role]);

  const canApprove    = user?.role !== 'employee' || hasDirectReports;
  const canSelfSubmit = true; // ALL roles can log their own hours

  // Employee-only view: only fetch /timesheets/my (not the full team view)
  // Admin/HR: fetch /timesheets (all) but ALSO can submit their own
  const isEmployee = user?.role === 'employee';

  const today = getLocalToday(); // FIX: local date, not UTC
  const nowDate = new Date();
  const [month,   setMonth]   = useState(nowDate.getMonth() + 1);
  const [year,    setYear]    = useState(nowDate.getFullYear());
  const [data,    setData]    = useState({ entries: [], timesheets: [], summary: {}, stats: {} });
  const [loading, setLoading] = useState(true);
  const [selected,setSelected]= useState([]);

  // Log hours form
  const [showLog,   setShowLog]   = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form,      setForm]      = useState({
    date: today, project: '', task_description: '', hours_worked: ''
  });
  const [saving,  setSaving]  = useState(false);
  const [errors,  setErrors]  = useState({});

  // Review modals
  const [approveModal, setApproveModal] = useState({ show: false, ids: [], bulk: false, loading: false, remarks: '' });
  const [rejectModal,  setRejectModal]  = useState({ show: false, id: null, loading: false, remarks: '' });

  const fetchTimesheets = useCallback(async () => {
    setLoading(true);
    setSelected([]);
    try {
      const canApproveNow = user?.role !== 'employee' || hasDirectReports;

      if (canApproveNow) {
        // Fetch team timesheets for approval AND own entries separately
        const [teamRes, myRes] = await Promise.all([
          axiosInstance.get('/timesheets', { params: { month, year } }),
          axiosInstance.get('/timesheets/my', { params: { month, year } }),
        ]);
        const teamData = teamRes.data.data || {};
        const myData   = myRes.data.data   || {};
        setData({
          ...teamData,
          entries:   teamData.entries || teamData.timesheets || [],
          myEntries: myData.entries   || [],
          summary:   teamData.summary || {},
          stats:     teamData.stats   || {},
        });
      } else {
        const res = await axiosInstance.get('/timesheets/my', { params: { month, year } });
        setData(res.data.data || {});
      }
    } catch { toast.error('Failed to load timesheets'); }
    finally { setLoading(false); }
  }, [month, year, isEmployee, hasDirectReports]);

  useEffect(() => { fetchTimesheets(); }, [fetchTimesheets]);

  const validateLog = () => {
    const e = {};
    if (!form.date)         e.date         = 'Date is required';
    if (!form.hours_worked) e.hours_worked = 'Hours is required';
    else if (parseFloat(form.hours_worked) <= 0 || parseFloat(form.hours_worked) > 24)
      e.hours_worked = 'Hours must be between 0.5 and 24';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validateLog();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axiosInstance.put(`/timesheets/${editingId}`, form);
        toast.success('Timesheet updated');
      } else {
        const res2 = await axiosInstance.post('/timesheets', form);
        const warning = res2?.data?.data?.warning;
        if (warning) toast.warning(warning);
        else toast.success('Timesheet entry saved');
      }
      setShowLog(false);
      setEditingId(null);
      setForm({ date: today, project: '', task_description: '', hours_worked: '' });
      setErrors({});
      fetchTimesheets();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/timesheets/${id}`);
      toast.success('Entry deleted');
      fetchTimesheets();
    } catch (err) { toast.error(err.response?.data?.message || 'Cannot delete approved entry'); }
  };

  const confirmApprove = async () => {
    setApproveModal(p => ({ ...p, loading: true }));
    try {
      if (approveModal.bulk) {
        await axiosInstance.post('/timesheets/bulk-approve', { ids: approveModal.ids });
        toast.success(`${approveModal.ids.length} entries approved`);
      } else {
        await axiosInstance.patch(`/timesheets/${approveModal.ids[0]}/review`, {
          status: 'approved', remarks: approveModal.remarks
        });
        toast.success('Timesheet approved');
      }
      setApproveModal({ show: false, ids: [], bulk: false, loading: false, remarks: '' });
      fetchTimesheets();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Approval failed');
      setApproveModal(p => ({ ...p, loading: false }));
    }
  };

  const confirmReject = async () => {
    if (!rejectModal.remarks?.trim()) {
      toast.error('Please provide a reason for rejecting this timesheet');
      return;
    }
    setRejectModal(p => ({ ...p, loading: true }));
    try {
      await axiosInstance.patch(`/timesheets/${rejectModal.id}/review`, {
        status: 'rejected', remarks: rejectModal.remarks
      });
      toast.success('Timesheet rejected');
      setRejectModal({ show: false, id: null, loading: false, remarks: '' });
      fetchTimesheets();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Rejection failed');
      setRejectModal(p => ({ ...p, loading: false }));
    }
  };

  const toggleSelect = (id) =>
    setSelected(p => p.includes(id) ? p.filter(i => i !== id) : [...p, id]);

  const navMonth = (dir) => {
    let m = month + dir, y = year;
    if (m > 12) { m = 1; y++; }
    if (m < 1)  { m = 12; y--; }
    setMonth(m); setYear(y);
  };

  const entries    = data.entries    || data.timesheets || []; // team entries
  const myEntries  = data.myEntries  || [];  // own entries (for canApprove users)
  const summary  = data.summary  || {};
  const stats    = data.stats    || {};
  const totalHrs = parseFloat(summary.total_hours || stats.approved_hours || 0);

  const pendingEntries = entries.filter(e => e.status === 'pending');

  const exportCSV = () => {
    const headers = ['Date','Employee','Project','Task','Hours','Status','Reviewer','Remarks'];
    const rows = entries.map(e => [
      e.date, `${e.first_name||''} ${e.last_name||''}`.trim() || 'Me',
      e.project||'', e.task_description||'', e.hours_worked,
      e.status, e.reviewer_name||'', e.remarks||''
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `Timesheets_${MONTHS[month-1]}_${year}.csv`;
    a.click();
  };

  const openLogModal = () => {
    setForm({ date: today, project: '', task_description: '', hours_worked: '' });
    setEditingId(null);
    setErrors({});
    setShowLog(true);
  };

  return (
    <div className="p-2 p-md-4">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h4 className="fw-bold mb-1"><Clock size={22} className="me-2 text-primary"/>Timesheets</h4>
          <p className="text-muted small mb-0">
            {/* FIX #4: ALL roles see "Log Hours" option */}
            {canApprove
              ? 'Review and approve timesheets — you can also log your own hours below'
              : 'Log and track your daily work hours'}
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          {canApprove && selected.length > 0 && (
            <Button variant="success" size="sm"
              onClick={() => setApproveModal({ show: true, ids: selected, bulk: true, loading: false, remarks: '' })}>
              <Check size={15} className="me-1"/>Approve Selected ({selected.length})
            </Button>
          )}
          <Button variant="outline-secondary" size="sm" onClick={exportCSV}>
            <Download size={15} className="me-1"/>Export CSV
          </Button>
          {/* FIX #4: Show "Log Hours" for ALL roles including admin */}
          {canSelfSubmit && (
            <Button variant="primary" onClick={openLogModal}>
              <Plus size={18} className="me-1"/>Log Hours
            </Button>
          )}
        </div>
      </div>

      {/* Month navigator */}
      <div className="d-flex align-items-center justify-content-center gap-3 mb-4">
        <Button variant="outline-secondary" size="sm" onClick={() => navMonth(-1)}>
          <ChevronLeft size={16}/>
        </Button>
        <h6 className="mb-0 fw-bold" style={{ minWidth: 130, textAlign: 'center' }}>
          {MONTHS[month-1]} {year}
        </h6>
        <Button variant="outline-secondary" size="sm" onClick={() => navMonth(1)}
          disabled={month === nowDate.getMonth()+1 && year === nowDate.getFullYear()}>
          <ChevronRight size={16}/>
        </Button>
      </div>

      {/* Summary cards */}
      <Row className="g-3 mb-4">
        {[
          { label: 'Total Hours',    value: `${totalHrs.toFixed(1)}h`,                                                         color: '#4f46e5' },
          { label: 'Approved Hours', value: `${parseFloat(summary.approved_hours || stats.approved_hours || 0).toFixed(1)}h`, color: '#059669' },
          { label: 'Pending',        value: isEmployee ? `${parseFloat(summary.pending_hours||0).toFixed(1)}h` : (stats.pending || 0), color: '#d97706' },
          { label: 'Entries',        value: entries.length,                                                                    color: '#0891b2' },
        ].map((s, i) => (
          <Col key={i} xs={6} lg={3}>
            <Card className="border-0 shadow-sm p-3 text-center">
              <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div className="text-muted small">{s.label}</div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Pending approval notice */}
      {canApprove && pendingEntries.length > 0 && (
        <Alert variant="warning" className="d-flex align-items-center gap-2 small mb-3">
          <AlertCircle size={16} className="flex-shrink-0"/>
          <span>
            <strong>{pendingEntries.length} entries</strong> pending approval for {MONTHS[month-1]} {year}.
            {selected.length === 0 && ' Select entries or approve individually.'}
          </span>
          {pendingEntries.length > 1 && selected.length === 0 && (
            <Button size="sm" variant="warning" className="ms-auto flex-shrink-0"
              onClick={() => setSelected(pendingEntries.map(e => e.id))}>
              Select All Pending
            </Button>
          )}
        </Alert>
      )}

      {/* Table */}
      <Card className="border-0 shadow-sm">
        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>
        ) : (
          <Table hover responsive className="mb-0 small align-middle">
            <thead className="bg-light">
              <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                {canApprove && (
                  <th className="ps-3" style={{ width: 36 }}>
                    <Form.Check
                      onChange={e => setSelected(e.target.checked ? pendingEntries.map(en => en.id) : [])}
                      checked={selected.length > 0 && selected.length === pendingEntries.length}
                      disabled={pendingEntries.length === 0}
                    />
                  </th>
                )}
                <th className="ps-3">Date</th>
                {canApprove && <th>Employee</th>}
                <th>Project</th>
                <th>Task</th>
                <th>Hours</th>
                <th>Status</th>
                {canApprove && <th>Remarks</th>}
                <th className="text-end pe-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.length > 0 ? entries.map(entry => (
                <tr key={entry.id}
                  style={{ background: entry.status === 'pending' && canApprove ? '#fffbeb' : undefined }}>
                  {canApprove && (
                    <td className="ps-3">
                      {entry.status === 'pending' && (
                        <Form.Check
                          checked={selected.includes(entry.id)}
                          onChange={() => toggleSelect(entry.id)}
                        />
                      )}
                    </td>
                  )}
                  <td className="ps-3 fw-medium">
                    {/* FIX #5: Use formatDateSafe — no UTC shift */}
                    {formatDateSafe(entry.date, { day: 'numeric', month: 'short' })}
                  </td>
                  {canApprove && (
                    <td>
                      <div className="fw-medium">{entry.first_name} {entry.last_name}</div>
                      {entry.employee_code && (
                        <div className="text-muted" style={{ fontSize: 10 }}>{entry.employee_code}</div>
                      )}
                    </td>
                  )}
                  <td>{entry.project || <span className="text-muted">—</span>}</td>
                  <td style={{ maxWidth: 200 }}>
                    <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {entry.task_description || <span className="text-muted">—</span>}
                    </div>
                  </td>
                  <td><strong>{parseFloat(entry.hours_worked).toFixed(1)}h</strong></td>
                  <td>
                    <Badge bg={STATUS_VARIANT[entry.status]} className="text-capitalize">
                      {entry.status}
                    </Badge>
                    {entry.reviewer_name && entry.status !== 'pending' && (
                      <div className="text-muted" style={{ fontSize: 10 }}>by {entry.reviewer_name}</div>
                    )}
                  </td>
                  {canApprove && (
                    <td style={{ maxWidth: 150 }}>
                      <div style={{ fontSize:11, color:'#64748b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {entry.remarks || '—'}
                      </div>
                    </td>
                  )}
                  <td className="text-end pe-3">
                    {/* Approve / Reject for managers */}
                    {canApprove && entry.status === 'pending' && (
                      <div className="d-flex justify-content-end gap-1">
                        <Button size="sm" variant="success" className="py-0 px-2" title="Approve"
                          onClick={() => setApproveModal({ show:true, ids:[entry.id], bulk:false, loading:false, remarks:'' })}>
                          <Check size={14}/> Approve
                        </Button>
                        <Button size="sm" variant="outline-danger" className="py-0 px-2" title="Reject"
                          onClick={() => setRejectModal({ show:true, id:entry.id, loading:false, remarks:'' })}>
                          <X size={14}/> Reject
                        </Button>
                      </div>
                    )}
                    {/* FIX #4: Edit + Cancel for OWN pending entries (ALL roles) */}
                    {entry.status === 'pending' && !canApprove && (
                      <div className="d-flex gap-1">
                        <Button size="sm" variant="outline-secondary" className="py-0 px-2"
                          title="Edit entry"
                          onClick={() => {
                            setForm({
                              date:             String(entry.date || '').slice(0, 10),
                              project:          entry.project          || '',
                              task_description: entry.task_description || '',
                              hours_worked:     String(entry.hours_worked),
                            });
                            setEditingId(entry.id);
                            setErrors({});
                            setShowLog(true);
                          }}>✏️
                        </Button>
                        <Button size="sm" variant="outline-danger" className="py-0"
                          title="Cancel entry"
                          onClick={() => handleDelete(entry.id)}>
                          <X size={13}/>
                        </Button>
                      </div>
                    )}
                    {entry.status !== 'pending' && (
                      <span className="text-muted" style={{ fontSize: 11 }}>—</span>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={canApprove ? 9 : 6} className="text-center py-5 text-muted">
                    <Clock size={36} className="d-block mx-auto mb-2 opacity-50"/>
                    No timesheet entries for {MONTHS[month-1]} {year}
                    {/* FIX #4: All roles can log hours */}
                    <div>
                      <Button variant="link" onClick={openLogModal}>Log your first entry</Button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </Card>

      {/* ── My Own Timesheet Entries (for approvers who also log their own hours) ── */}
      {canApprove && myEntries.length > 0 && (
        <Card className="border-0 shadow-sm mt-4">
          <Card.Header className="bg-white border-0 fw-bold pt-3">
            My Own Timesheets — {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][month-1]} {year}
          </Card.Header>
          <Table hover responsive className="mb-0 small align-middle">
            <thead className="bg-light">
              <tr className="text-muted text-uppercase" style={{ fontSize: 11 }}>
                <th className="ps-3">Date</th>
                <th>Project</th>
                <th>Task</th>
                <th>Hours</th>
                <th>Status</th>
                <th className="text-end pe-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {myEntries.map(entry => (
                <tr key={`my-${entry.id}`}>
                  <td className="ps-3 fw-medium">
                    {(() => { const s=String(entry.date||'').slice(0,10); const [y,m,d]=s.split('-').map(Number); return new Date(y,m-1,d).toLocaleDateString('en-IN',{day:'numeric',month:'short'}); })()}
                  </td>
                  <td>{entry.project || <span className="text-muted">—</span>}</td>
                  <td style={{ maxWidth:200 }}><div style={{ overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{entry.task_description || <span className="text-muted">—</span>}</div></td>
                  <td><strong>{parseFloat(entry.hours_worked).toFixed(1)}h</strong></td>
                  <td><Badge bg={STATUS_VARIANT[entry.status]} className="text-capitalize">{entry.status}</Badge></td>
                  <td className="text-end pe-3">
                    {entry.status === 'pending' && (
                      <div className="d-flex justify-content-end gap-1">
                        <Button size="sm" variant="outline-secondary" className="py-0 px-2"
                          onClick={() => {
                            setForm({ date:String(entry.date||'').slice(0,10), project:entry.project||'', task_description:entry.task_description||'', hours_worked:String(entry.hours_worked) });
                            setEditingId(entry.id); setErrors({}); setShowLog(true);
                          }}>✏️</Button>
                        <Button size="sm" variant="outline-danger" className="py-0" onClick={() => handleDelete(entry.id)}><X size={13}/></Button>
                      </div>
                    )}
                    {entry.status !== 'pending' && <span className="text-muted" style={{fontSize:11}}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* ── Log Hours Modal — shown for ALL roles ── */}
      <Modal show={showLog} onHide={() => { setShowLog(false); setEditingId(null); setErrors({}); }} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold">
            <Clock size={18} className="me-2 text-primary"/>
            {editingId ? 'Edit Timesheet Entry' : 'Log Work Hours'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form noValidate onSubmit={handleSubmit}>
            <Row className="g-3">
              <Col xs={12} sm={6}>
                <Form.Label className="small fw-bold">Date *</Form.Label>
                {/* FIX #5: min/max use getLocalDateBefore (not toISOString which shifts UTC) */}
                <Form.Control type="date" required
                  value={form.date}
                  min={getLocalDateBefore(14)}
                  max={today}
                  onChange={e => {
                    setForm(p => ({ ...p, date: e.target.value }));
                    setErrors(p => ({ ...p, date: '' }));
                  }}
                  isInvalid={!!errors.date}
                />
                <Form.Control.Feedback type="invalid">{errors.date}</Form.Control.Feedback>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Label className="small fw-bold">Hours Worked *</Form.Label>
                <Form.Control type="number" required min="0.5" max="24" step="0.5" placeholder="e.g. 8"
                  value={form.hours_worked}
                  onChange={e => {
                    setForm(p => ({ ...p, hours_worked: e.target.value }));
                    setErrors(p => ({ ...p, hours_worked: '' }));
                  }}
                  isInvalid={!!errors.hours_worked}
                />
                <Form.Control.Feedback type="invalid">{errors.hours_worked}</Form.Control.Feedback>
              </Col>
              <Col xs={12}>
                <Form.Label className="small fw-bold">Project / Client</Form.Label>
                <Form.Control placeholder="e.g. PeoplesDock Sprint 4"
                  value={form.project}
                  onChange={e => setForm(p => ({ ...p, project: e.target.value }))}
                />
              </Col>
              <Col xs={12}>
                <Form.Label className="small fw-bold">Task Description</Form.Label>
                <Form.Control as="textarea" rows={3} placeholder="What did you work on today?"
                  value={form.task_description}
                  onChange={e => setForm(p => ({ ...p, task_description: e.target.value }))}
                />
              </Col>
            </Row>
            <div className="d-flex gap-2 mt-4">
              <Button variant="light" type="button" className="flex-grow-1"
                onClick={() => { setShowLog(false); setEditingId(null); setErrors({}); }}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" className="flex-grow-1 fw-bold" disabled={saving}>
                {saving ? <Spinner size="sm"/> : 'Save Entry'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* ── Approve Confirm Modal ── */}
      <ConfirmModal
        show={approveModal.show}
        onHide={() => setApproveModal({ show:false, ids:[], bulk:false, loading:false, remarks:'' })}
        onConfirm={confirmApprove}
        loading={approveModal.loading}
        title={approveModal.bulk ? `Approve ${approveModal.ids.length} Entries` : 'Approve Timesheet'}
        message={approveModal.bulk
          ? `Approve ${approveModal.ids.length} selected timesheet entries?`
          : 'Approve this timesheet entry?'}
        confirmLabel={approveModal.bulk ? 'Yes, Approve All' : 'Yes, Approve'}
        variant="success"
        showRemarks={!approveModal.bulk}
        remarks={approveModal.remarks}
        onRemarksChange={v => setApproveModal(p => ({ ...p, remarks: v }))}
      />

      {/* ── Reject Confirm Modal ── */}
      <ConfirmModal
        show={rejectModal.show}
        onHide={() => setRejectModal({ show:false, id:null, loading:false, remarks:'' })}
        onConfirm={confirmReject}
        loading={rejectModal.loading}
        title="Reject Timesheet"
        message="Reject this timesheet entry? The employee will be notified."
        confirmLabel="Yes, Reject"
        variant="danger"
        showRemarks
        remarks={rejectModal.remarks}
        onRemarksChange={v => setRejectModal(p => ({ ...p, remarks: v }))}
      />
    </div>
  );
};

export default TimesheetPage;
